import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { BaseBillingDialog } from '../base-billing-dialog';
import { SubscriptionTier } from '@core/models/billing/subscription-tier';
import { TabItem } from '@components/tabs/tab-item.interface';
import { PlaidSuccessEvent } from '@components/plaid-link/plaid-success.event';
import { PlaidSuccessMetadata } from '@components/plaid-link/interfaces';
import { EnvironmentConfigTierType } from '@environments/environment-config-type';
import { SubscriptionPlan } from '@core/models/billing/subscription-plan';
import { BillingService } from '@features/billing/billing.service';
import { ToastService } from '@core/state/toast/toast.service';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { DialogRef } from '@components/dialog/dialog-ref';
import { getTierConfig } from '@core/utils/billing';
import { SubscriptionInterval } from '@core/models/billing/subscription-interval';
import { takeUntil } from 'rxjs';
import { BillingDetailRequest } from '@core/models/billing/billing-detail-request';
import { pickClass } from '@core/utils/object';

@Component({
    selector: 'ihd-dialog-subscribe',
    templateUrl: 'subscribe.component.html',
    styleUrls: ['./subscribe.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'c-dialog-subscribe',
        '[class.c-dialog-subscribe--split]': 'this.config.showPlanDetails',
    },
})
export class DialogSubscribeComponent extends BaseBillingDialog implements OnInit, OnDestroy, AfterViewInit {
    public readonly SubscriptionTier: any = SubscriptionTier;

    public submitKey: string;
    public config: any;
    public tabItems: TabItem[];
    public upsellTier: SubscriptionTier;
    public isAnnualMode: boolean;
    public accountDetails: PlaidSuccessEvent;
    public accountDetailsMetadata: PlaidSuccessMetadata;

    private tierConfig: EnvironmentConfigTierType;
    private planId: SubscriptionPlan;
    private configPlanId: SubscriptionPlan;

    constructor(
        billingService: BillingService,
        cdr: ChangeDetectorRef,
        toastService: ToastService,
        sanitizer: DomSanitizer,
        translateService: TranslateService,
        private dialogRef: DialogRef<DialogSubscribeComponent>
    ) {
        super(
            billingService,
            toastService,
            sanitizer,
            translateService,
            cdr
        );
        this.config = dialogRef.containerInstance.config;
        this.upsellTier = this.config.tier;
        this.tierConfig = getTierConfig(this.upsellTier);
        this.submitKey = 'Billing.Subscription.BuyNow';
        this.configPlanId = this.config.planId;
        this.planId = this.configPlanId;
        this.isAnnualMode = true;
        if (this.config.interval) {
            this.isAnnualMode =
                this.config.interval === SubscriptionInterval.YEAR;
        }

        const tierLabel: string = this.translateService.instant(
            `Plans.${this.upsellTier}`
        );

        let panelLabel: string = this.translateService.instant(
            'Billing.Subscription.BillingDetails',
        );

        if (this.config.onlyBilling) {
            panelLabel = this.translateService.instant(
                'Billing.Details.Subscribe.AddBillingDetails'
            );
            this.submitKey = 'Common.Confirm';
        }

        this.tabItems = [
            {
                label: panelLabel,
                active: true,
            },
        ];
        this.createForm();
    }

    ngOnInit() {
        this.init();

        this.billingService
            .isIHDSubscription()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((isIHDSubscription: boolean) => {
                if (!isIHDSubscription) {
                    this.initStripe();
                } else {
                    this.amendDialogLabelsForIHDSubscription();
                    this.cdr.markForCheck();
                }
            });

        // Define the existing billing details
        const billingDetails: BillingDetailRequest = pickClass(
            this.config.billingDetails || {},
            BillingDetailRequest
        );
        this.billingForm.reset(billingDetails);
        this.cdr.markForCheck();
    }

    ngOnDestroy() {
        this.destroy();
    }

    ngAfterViewInit() {
        this.isFormReady = true;

        // Wait for Stripe and form to both be ready
        if (this.isStripeReady) {
            this.initStripeForm();
        } else if (!this.isIHDSubscription) {
            // if stripe hasn't initalized yet, its time to retry
            this.retryInit();
        }
    }

    setAccountMeta(details: PlaidSuccessEvent) {
        this.accountDetails = details;
        this.accountDetailsMetadata = details?.metadata;
    }

    closeDialog() {
        this.dialogRef.close();
    }

    setPlan(interval: SubscriptionInterval) {
        if (interval === this.config.interval) {
            // if using the same interval, then respect the planId of the subscription sent in the config
            this.planId = this.configPlanId;
        } else {
            this.planId = this.tierConfig[interval].planId;
        }
    }

    submitWithDirectDebit() {
        this.isSubmitted = true;
        this.cdr.markForCheck();

        if (this.billingForm.valid) {
            const billingDetails = this.billingForm.value;
            billingDetails.plan = this.planId;
            billingDetails.end_trial = true;

            this.isLoading = true;
            this.cdr.markForCheck();

            // If stripe success then process token
            if (this.accountDetailsMetadata) {
                billingDetails.plaid_source = this.accountDetailsMetadata.public_token;

                if (this.accountDetailsMetadata.account) {
                    billingDetails.plaid_account = this.accountDetailsMetadata.account.id;
                }

                this.createOrUpdateSubscription(billingDetails);
            } else {
                // ERROR
                this.isLoading = true;
            }
        }
    }

    /**
     * Create card token then save details.
     * @returns {Promise<void>}
     */
    async submitWithCreditCard() {
        this.isSubmitted = true;
        this.cdr.markForCheck();

        if (this.billingForm.valid) {
            const billingDetails = this.billingForm.value;
            billingDetails.plan = this.planId;
            billingDetails.end_trial = true;

            this.isLoading = true;
            this.cdr.markForCheck();

            let stripeResponse = await this.stripe.createToken(
                this.cardNumber,
                {
                    name: `${billingDetails.first_name} ${billingDetails.last_name}`,
                    address_line1: billingDetails.address.line1,
                    address_line2: billingDetails.address.line2,
                    address_city: billingDetails.address.city,
                    address_state: billingDetails.address.state,
                    address_zip: billingDetails.address.postal_code,
                    address_country: billingDetails.country,
                }
            );

            // If stripe success then process token
            if (stripeResponse && stripeResponse.token) {
                billingDetails.source = stripeResponse.token.id;
            }

            if (stripeResponse) {
                if (!stripeResponse.error) {
                    this.createOrUpdateSubscription(billingDetails);
                } else {
                    // Reset the loading flag, during the validation error
                    this.isLoading = false;
                    this.cdr.markForCheck();
                    this.toastService.showError(stripeResponse.error.message);

                    // If it's a validation error, handle in the dialog box. Else, show an error message
                    if (stripeResponse.error.type !== 'validation_error') {
                        this.dialogRef.close({
                            success: false,
                            response: stripeResponse.error,
                        });
                    }
                }
            } else {
                // ERROR: Stripe failed
                this.isLoading = true;
            }
        }
    }

    private createOrUpdateSubscription(billingDetails) {
        if (this.currentSubscription) {
            // Update existing subscription
            this.billingService.updateBilling(billingDetails).subscribe(
                (response) => {
                    this.dialogRef.close({
                        success: true,
                        response,
                    });
                },
                (response) => {
                    this.processResponse(response);
                }
            );
        } else {
            // New subscription
            this.billingService.createBilling(billingDetails).subscribe(
                (response) => {
                    this.dialogRef.close({
                        success: true,
                        response,
                    });
                },
                (response) => {
                    this.processResponse(response);
                }
            );
        }
    }

    private processResponse(response) {
        // ERROR: Update failed.
        // Define payment gateway error as required
        let billingError: any;
        try {
            billingError = JSON.parse(response.payload.error.message);
        } catch (e) {
            billingError = response.payload.error;
        }

        this.toastService.showError(billingError.message);
        this.isLoading = false;
        this.cdr.markForCheck();
    }

    private amendDialogLabelsForIHDSubscription() {
        this.tabItems[0].label = this.translateService.instant(
            'Billing.Details.Title'
        );
    }
}
